import { __awaiter } from "tslib";
import { ccApi } from './ccApi';
import { CC_API_TAGS } from './constants';
/** Path segment for the template resource (no areaId prefix). */
const TEMPLATE_PATH = '/template';
/** Path segment for the image resource. */
const IMAGE_PATH = '/image';
/**
 * RTK Query endpoints for template and image data.
 *
 * This module extends the base ccApi with template CRUD and image
 * query/mutation endpoints.
 *
 * Template endpoints sit directly under `/template` (no areaId prefix).
 * Image endpoints sit under `/image`.
 *
 * @see https://redux-toolkit.js.org/rtk-query/api/created-api/code-splitting
 */
export const templateApi = ccApi.injectEndpoints({
    endpoints: (builder) => ({
        // -----------------------------------------------------------------------
        // Template queries
        // -----------------------------------------------------------------------
        /**
         * Fetches the full list of templates.
         *
         * @endpoint GET /template
         */
        getTemplateList: builder.query({
            query: () => TEMPLATE_PATH,
            providesTags: [CC_API_TAGS.TemplateList],
        }),
        /**
         * Fetches a single template by ID.
         *
         * @endpoint GET /template/{id}
         */
        getTemplateById: builder.query({
            query: ({ id }) => `${TEMPLATE_PATH}/${id}`,
            providesTags: (_result, _error, { id }) => [{ type: CC_API_TAGS.Template, id }],
        }),
        // -----------------------------------------------------------------------
        // Template mutations
        // -----------------------------------------------------------------------
        /**
         * Creates a new template.
         *
         * @endpoint POST /template
         */
        createTemplate: builder.mutation({
            query: ({ data }) => ({
                url: TEMPLATE_PATH,
                method: 'POST',
                body: data,
            }),
            invalidatesTags: [CC_API_TAGS.TemplateList],
        }),
        /**
         * Updates an existing template.
         *
         * @endpoint PUT /template/{id}
         */
        updateTemplate: builder.mutation({
            query: ({ data }) => ({
                url: `${TEMPLATE_PATH}/${data.id}`,
                method: 'PUT',
                body: data,
            }),
            invalidatesTags: (_result, _error, { data }) => [
                CC_API_TAGS.TemplateList,
                { type: CC_API_TAGS.Template, id: data.id },
            ],
        }),
        /**
         * Deletes a single template.
         *
         * @endpoint DELETE /template/{id}
         */
        deleteTemplate: builder.mutation({
            query: ({ id }) => ({
                url: `${TEMPLATE_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: (_result, _error, { id }) => [
                CC_API_TAGS.TemplateList,
                { type: CC_API_TAGS.Template, id },
            ],
        }),
        /**
         * Bulk-deletes templates by issuing individual DELETE calls.
         *
         * There is no bulk-delete API endpoint, so this uses a custom
         * `queryFn` with a `Promise.allSettled` pattern.
         *
         * @returns `{ succeeded, failed }` so consumers can report per-item errors.
         */
        bulkDeleteTemplate: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ ids }, _queryApi, _extraOptions, baseQuery) {
                    const results = yield Promise.allSettled(ids.map((id) => Promise.resolve(baseQuery({
                        url: `${TEMPLATE_PATH}/${id}`,
                        method: 'DELETE',
                    }))));
                    const succeeded = [];
                    const failed = [];
                    results.forEach((result, index) => {
                        var _a, _b;
                        if (result.status === 'fulfilled' && !result.value.error) {
                            succeeded.push(ids[index]);
                        }
                        else {
                            const errorMessage = result.status === 'rejected'
                                ? String(result.reason)
                                : JSON.stringify((_b = (_a = result.value.error) === null || _a === void 0 ? void 0 : _a.data) !== null && _b !== void 0 ? _b : 'Unknown error');
                            failed.push({ id: ids[index], error: errorMessage });
                        }
                    });
                    return { data: { succeeded, failed } };
                });
            },
            invalidatesTags: [CC_API_TAGS.TemplateList],
        }),
        // -----------------------------------------------------------------------
        // Image queries
        // -----------------------------------------------------------------------
        /**
         * Fetches all available images.
         *
         * Renamed from the misleading `getTemplateImageById` — this endpoint
         * fetches the complete image list with no id parameter.
         *
         * @endpoint GET /image
         */
        getImageList: builder.query({
            query: () => IMAGE_PATH,
            providesTags: [CC_API_TAGS.ImageList],
        }),
        // -----------------------------------------------------------------------
        // Image mutations
        // -----------------------------------------------------------------------
        /**
         * Uploads a new image via multipart form data.
         *
         * Uses a custom `queryFn` to construct the FormData body matching
         * the shape expected by the backend (`file`, `name`, `type`,
         * `defaultImage` fields).
         *
         * @endpoint POST /image
         */
        uploadImage: builder.mutation({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ file }, _queryApi, _extraOptions, baseQuery) {
                    var _b;
                    const formData = new FormData();
                    formData.append('file', file);
                    formData.append('name', file.name);
                    formData.append('type', (_b = file.type.split('image/')[1]) !== null && _b !== void 0 ? _b : '');
                    formData.append('defaultImage', 'false');
                    const result = yield baseQuery({
                        url: IMAGE_PATH,
                        method: 'POST',
                        body: formData,
                        // Do NOT set Content-Type — the browser sets the multipart boundary automatically
                    });
                    if (result.error) {
                        return { error: result.error };
                    }
                    return { data: result.data };
                });
            },
            invalidatesTags: [CC_API_TAGS.ImageList],
        }),
        /**
         * Deletes a single image by ID.
         *
         * @endpoint DELETE /image/{id}
         */
        deleteImage: builder.mutation({
            query: ({ id }) => ({
                url: `${IMAGE_PATH}/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [CC_API_TAGS.ImageList],
        }),
    }),
});
export const { useBulkDeleteTemplateMutation, useCreateTemplateMutation, useDeleteImageMutation, useDeleteTemplateMutation, useGetImageListQuery, useGetTemplateByIdQuery, useGetTemplateListQuery, useUpdateTemplateMutation, useUploadImageMutation, } = templateApi;
